/**
 * SSR- and SecurityError-safe localStorage access.
 *
 * Reading the `window.localStorage` PROPERTY itself throws a SecurityError when
 * the browser blocks storage — strict privacy mode, third-party/blocked cookies,
 * sandboxed or embedded contexts. An unguarded `localStorage.getItem(...)` in a
 * render or effect then crashes the React tree all the way to the root error
 * boundary ("Failed to read the 'localStorage' property from 'Window': Access is
 * denied for this document" — prod /fr/analyse-cv, 2026-06-23).
 *
 * These helpers make every access TOTAL: the property read, the operation, and
 * any quota error are all caught, degrading to null / no-op instead of throwing.
 * Use them everywhere instead of touching localStorage directly.
 */
export function safeGetItem(key: string): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

export function safeSetItem(key: string, value: string): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* storage blocked / quota exceeded — degrade silently */
  }
}

export function safeRemoveItem(key: string): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* storage blocked — nothing to remove */
  }
}

// sessionStorage throws the same SecurityError on a blocked-storage document.
export function safeSessionGetItem(key: string): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

export function safeSessionSetItem(key: string, value: string): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    /* storage blocked / quota — degrade silently */
  }
}
